import * as React from "react";
import { Button, FormControl, FormGroup, ControlLabel, Alert, Panel, ButtonToolbar, Glyphicon } from "react-bootstrap";
import { IContactListResponse } from "@nkr/store/index";
import { ITicket, IUser, ISelectItem, ITicketCannedResponse } from "@nkr/models/index";
import JoditEditor from "@nkr/components/editor";
import config from '../jodit-editor-config';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Select, { InputActionMeta, OptionsType } from 'react-select';
import { DropdownIndicator } from '../dropdown-indicator';
import MyUploader from "../file-upload";
import { TicketVisibility } from "@nkr/enums/index";
import { helpers } from "./helpers";
import { debounce } from "lodash";

const editorConfig = { ...config, height: '250px' };

interface IProps {
    show: boolean,
    user: IUser,
    ticket: ITicket,
    cannedResponse: ITicketCannedResponse,
    onGetContacts: (search: string) => Promise<IContactListResponse>,
    onShowCannedResponses: () => void,
    onSave: (from: string, to: ISelectItem[], cc: ISelectItem[], bcc: ISelectItem[], subject: string, message: string, visibility: TicketVisibility, files: File[]) => void,
    onCancel: () => void
}

interface IState {
    fromList: ISelectItem[],
    from: ISelectItem,
    showToOptions: boolean,
    to: ISelectItem[],
    cc: ISelectItem[],
    includeCc: boolean,
    showCcOptions: boolean,
    bcc: ISelectItem[],
    includeBcc: boolean,
    showBccOptions: boolean,
    message: string,
    files: File[],
    valid: {
        to: boolean,
        message: boolean
    }
}

const getInitialMessage = (props: IProps) => {
    if (props.ticket != null) {
        let { id, createdDateTime, messages } = props.ticket;
        let { firstName, lastName, email } = props.user;

        let message = `Please take a look at ticket <a href='https://${__ADMIN__}/ticketing/ticket/${id}'>#${id}</a> raised by ${firstName} ${lastName} (${email})`;

        return message;
    }

    return "";
}

class Forward extends React.Component<IProps, IState> {
    readonly state: IState = {
        fromList: helpers.getFromList(this.props.user.firstName, this.props.user.lastName, this.props.user.username, this.props.user.email),
        from: helpers.getFromList(this.props.user.firstName, this.props.user.lastName, this.props.user.username, this.props.user.email)[0],
        showToOptions: false,
        to: [],
        cc: [],
        includeCc: false,
        showCcOptions: false,
        bcc: [],
        includeBcc: false,
        showBccOptions: false,
        message: getInitialMessage(this.props),
        files: [],
        valid: {
            to: true,
            message: true
        }
    }

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.cannedResponse != null) {
            this.setState({
                message: nextProps.cannedResponse.message
            });
        }
    }

    handleFromChange = (from: ISelectItem) => {
        this.setState({
            from
        });
    }

    handleToInputChange = (value: string, action: InputActionMeta) => {
        this.setState({
            showToOptions: value.length >= 3
        });
    }

    handleToChange = (to: ISelectItem[]) => {
        this.setState({
            to
        });
    }

    handleCcInputChange = (value: string, action: InputActionMeta) => {
        this.setState({
            showCcOptions: value.length >= 3
        });
    }

    handleIncludeCc = () => {
        this.setState({
            includeCc: true
        });
    }

    handleBccInputChange = (value: string, action: InputActionMeta) => {
        this.setState({
            showBccOptions: value.length >= 3
        });
    }

    handleIncludeBcc = () => {
        this.setState({
            includeBcc: true
        });
    }

    handleCcChange = (cc: ISelectItem[]) => {
        this.setState({
            cc
        });
    }

    handleBccChange = (bcc: ISelectItem[]) => {
        this.setState({
            bcc
        });
    }

    handleMessageChange = (message: string) => {
        this.setState({
            message
        });
    }

    handleFilesChanged = (file: File, status: string) => {
        let files = [...this.state.files];

        if (status == 'done') {
            files.push(file);
        }

        if (status == 'removed') {
            files = files.filter(x => x.name != file.name);
        }

        this.setState({
            files
        });
    }

    handleSave = () => {
        let { from, to, cc, bcc, message, files } = this.state;

        let validTo = to != null;
        let validMessage = message != "";

        this.setState({
            valid: {
                to: validTo,
                message: validMessage
            }
        }, () => {
            if (validTo && validMessage)
                this.props.onSave(from.email, to, cc, bcc, 'FW: ' + this.props.ticket.subject, message, TicketVisibility.Public, files);
        });
    }

    handleCancel = () => {
        this.props.onCancel();
    }

    loadContactOptions = debounce((search: string, callback: ((options: OptionsType<any>) => void)) => {
        if (search.length >= 3) {
            this.props.onGetContacts(search)
                .then(response => {
                    if (response.data.success) {
                        let results = response.data.results.map(x => { return { value: x.id, label: `${x.firstName + ' ' + x.lastName} (${x.email})`, email: x.email }; });
                        callback(results);
                    }
                })
                .catch((error) => {
                    callback(error);
                });
        } else {
            callback([]);
        }
    }, 500)

    render = () => {
        return (
            <>
                <Panel>
                    <Panel.Heading style={{ backgroundColor: "#f5f7f9", borderBottom: '0 none' }}>
                        <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                            <div style={{ marginRight: 5 }}>From:</div>
                            <Select
                                isSearchable={false}
                                styles={{
                                    container: defaultStyles => ({
                                        ...defaultStyles,
                                        width: '100%',
                                        height: 34,
                                        minHeight: 34
                                    }),
                                    control: defaultStyles => ({
                                        ...defaultStyles,
                                        height: 34,
                                        minHeight: 34
                                    }),
                                    indicatorSeparator: defaultStyles => ({ ...defaultStyles }),
                                    dropdownIndicator: defaultStyles => ({
                                        ...defaultStyles,
                                        color: 'black'
                                    })
                                }}
                                components={{ DropdownIndicator }}
                                options={this.state.fromList}
                                value={this.state.from}
                                onChange={this.handleFromChange}
                            />
                        </FormGroup>
                    </Panel.Heading>
                    <Panel.Heading style={{ backgroundColor: "#f5f7f9", borderBottom: '0 none' }}>
                        <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: this.state.includeCc || this.state.includeBcc ? 5 : 0 }}>
                            <div style={{ marginRight: 5 }}>To:</div>
                            <div style={{ width: '100%' }}>
                                <AsyncCreatableSelect
                                    value={this.state.to}
                                    isMulti={true}
                                    loadOptions={this.state.showToOptions ? this.loadContactOptions : null}
                                    components={{ DropdownIndicator }}
                                    styles={{
                                        indicatorSeparator: defaultStyles => ({ ...defaultStyles }),
                                        dropdownIndicator: defaultStyles => ({
                                            ...defaultStyles,
                                            color: 'black'
                                        }),
                                        menu: defaultStyles => ({
                                            ...defaultStyles,
                                            zIndex: 99999
                                        }),
                                        menuList: defaultStyles => ({
                                            ...defaultStyles
                                        })
                                    }}
                                    onInputChange={this.handleToInputChange}
                                    onChange={this.handleToChange}
                                />
                            </div>
                            <div style={{ display: 'flex', marginLeft: 5 }}>
                                <Button bsStyle="link" bsSize="xs" onClick={this.handleIncludeCc}>Cc</Button>
                                <Button bsStyle="link" bsSize="xs" onClick={this.handleIncludeBcc}>Bcc</Button>
                            </div>
                        </FormGroup>

                        {this.state.includeCc && (
                            <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: this.state.includeBcc ? 5 : 0 }}>
                                <div style={{ marginRight: 5 }}>Cc:</div>
                                <div style={{ width: '100%' }}>
                                    <AsyncCreatableSelect
                                        value={this.state.cc}
                                        isMulti={true}
                                        loadOptions={this.state.showCcOptions ? this.loadContactOptions : null}
                                        components={{ DropdownIndicator }}
                                        styles={{
                                            indicatorSeparator: defaultStyles => ({ ...defaultStyles }),
                                            dropdownIndicator: defaultStyles => ({
                                                ...defaultStyles,
                                                color: 'black'
                                            }),
                                            menu: defaultStyles => ({
                                                ...defaultStyles,
                                                zIndex: 99999
                                            }),
                                            menuList: defaultStyles => ({
                                                ...defaultStyles
                                            })
                                        }}
                                        onInputChange={this.handleCcInputChange}
                                        onChange={this.handleCcChange}
                                    />
                                </div>
                            </FormGroup>
                        )}

                        {this.state.includeBcc && (
                            <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                <div style={{ marginRight: 5 }}>Bcc</div>
                                <div style={{ width: '100%' }}>
                                    <AsyncCreatableSelect
                                        value={this.state.bcc}
                                        isMulti={true}
                                        loadOptions={this.state.showBccOptions ? this.loadContactOptions : null}
                                        components={{ DropdownIndicator }}
                                        styles={{
                                            indicatorSeparator: defaultStyles => ({ ...defaultStyles }),
                                            dropdownIndicator: defaultStyles => ({
                                                ...defaultStyles,
                                                color: 'black'
                                            }),
                                            menu: defaultStyles => ({
                                                ...defaultStyles,
                                                zIndex: 99999
                                            }),
                                            menuList: defaultStyles => ({
                                                ...defaultStyles
                                            })
                                        }}
                                        onInputChange={this.handleBccInputChange}
                                        onChange={this.handleBccChange}
                                    />
                                </div>
                            </FormGroup>
                        )}
                    </Panel.Heading>

                    <Panel.Body>
                        {(!this.state.valid.to || !this.state.valid.message) && (
                            <Alert bsStyle="danger">
                                Please fix the items highlighted in <strong>red</strong> below...
                            </Alert>
                        )}

                        <FormGroup validationState={this.state.valid.message ? null : "error"}>
                            <ControlLabel>Message</ControlLabel>
                            <JoditEditor
                                value={this.state.message}
                                config={editorConfig}
                                setFocus={true}
                                onChange={this.handleMessageChange}
                                onBlur={() => null}
                            />

                            {!this.state.valid.message && (
                                <span className="help-block">Please enter the contents of the message...</span>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                                <div>
                                    <Button bsSize='xs' title='Canned Responses' onClick={this.props.onShowCannedResponses}><Glyphicon glyph='comment' /></Button>
                                </div>
                                <div>
                                    <MyUploader filesChanged={this.handleFilesChanged} />
                                </div>
                            </div>
                        </FormGroup>

                        <ButtonToolbar style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button bsStyle="primary" onClick={this.handleSave}>Forward</Button>
                            <Button onClick={this.handleCancel}>Cancel</Button>
                        </ButtonToolbar>
                    </Panel.Body>
                </Panel>
            </>
        );
    }
}

export default Forward;